import { useEffect } from 'react';

import cloneDeep from 'lodash/cloneDeep';

import { ClickHeuristic, DomMutationHeuristic, EventAttributes } from './types';

// Global attributes attached to all events
const globalAttributes: Record<string, string | number | boolean> = {};

export const setAnalyticsGlobal = (
  key: string,
  value?: string | number | boolean | null
) => {
  if (value == null) {
    delete globalAttributes[key];
  } else {
    globalAttributes[key] = value;
  }
};

export const deleteAnalyticsGlobal = (key: string) => {
  delete globalAttributes[key];
};

export const getAnalyticsGlobal = (key: string) => {
  return globalAttributes[key];
};

export const getGlobalsSnapshot = () => {
  return cloneDeep(globalAttributes);
};

const counterOfRenderedHooksPerKey = new Map<string, number>();

type UseAnalyticsGlobalOptions = {
  enabled?: boolean;
};

export const useSetAnalyticsGlobal = (
  key: string,
  value?: string | number | boolean | null,
  { enabled = true }: UseAnalyticsGlobalOptions = {}
) => {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const count = counterOfRenderedHooksPerKey.get(key);
    if ((count || 0) > 0) {
      console.error(`Multiple active hooks per analytics key "${key}"`);
    } else {
      counterOfRenderedHooksPerKey.set(key, 1);
    }
    return () => {
      counterOfRenderedHooksPerKey.delete(key);
    };
  }, [key, enabled]);

  // Don't put it inside `useEffect`. Child components may use that data when rendered.
  if (enabled) {
    if (value === undefined) {
      deleteAnalyticsGlobal(key);
    } else {
      setAnalyticsGlobal(key, value);
    }
  }

  useEffect(() => {
    return () => {
      deleteAnalyticsGlobal(key);
    };
  }, [key]);
};

// Global attributes resolver — a callback that returns DOM-resolved attributes (e.g. page header, sidebar selection)
type GlobalAttributesResolver = () => EventAttributes;

let globalAttributesResolver: GlobalAttributesResolver | undefined;

export const setGlobalAttributesResolver = (
  resolver: GlobalAttributesResolver
) => {
  globalAttributesResolver = resolver;
};

export const getGlobalAttributesResolver = () => globalAttributesResolver;

// Heuristics
let clickHeuristics: ClickHeuristic[] = [];
let domMutationHeuristics: DomMutationHeuristic[] = [];

export const setHeuristics = (heuristics: {
  click?: ClickHeuristic[];
  domMutation?: DomMutationHeuristic[];
}) => {
  if (heuristics.click) {
    clickHeuristics = heuristics.click;
  }
  if (heuristics.domMutation) {
    domMutationHeuristics = heuristics.domMutation;
  }
};

export function addHeuristic(type: 'click', heuristic: ClickHeuristic): void;
export function addHeuristic(
  type: 'domMutation',
  heuristic: DomMutationHeuristic
): void;
export function addHeuristic(
  type: 'click' | 'domMutation',
  heuristic: ClickHeuristic | DomMutationHeuristic
) {
  if (type === 'click') {
    clickHeuristics = [...clickHeuristics, heuristic as ClickHeuristic];
  } else {
    domMutationHeuristics = [
      ...domMutationHeuristics,
      heuristic as DomMutationHeuristic,
    ];
  }
}

export const getClickHeuristics = () => clickHeuristics;
export const getDomMutationHeuristics = () => domMutationHeuristics;

// Global error handler
type ErrorHandler = (error: unknown) => void;

let globalErrorHandler: ErrorHandler | undefined;

export const setGlobalErrorHandler = (handler: ErrorHandler) => {
  globalErrorHandler = handler;
};

export const removeGlobalErrorHandler = () => {
  globalErrorHandler = undefined;
};

export const getGlobalErrorHandler = () => globalErrorHandler;
